// ── BCHNostr — Standalone embeddable Creator Support widget ───────
//
// Rendered CHROME-FREE inside an <iframe> on any external website. It shows
// live data for one creator's Support Goal (the existing kind 30078
// `bch-creator-goal` + their on-chain balance + kind 9735 goal pledges) and a
// "Donate with BCH" button that opens the creator's support page — so payments
// stay non-custodial and flow through the existing Creator Support system.
// Nothing here handles funds.
//
// The widget UI itself lives in components/GoalWidget.tsx so the embed builder
// can render an identical LIVE preview inline (without booting a second copy
// of the app inside a nested iframe). This page is the data + analytics +
// auto-resize shell around it.
//
// The page reports its rendered height to the parent via postMessage so the
// copy/paste snippet can size the iframe with no scrollbars, and it records a
// view (and each donate click) as an anonymous kind 7027 analytics ping.

import { useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useGoalEmbedData } from '../hooks/useGoalEmbedData';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { GoalWidget } from '../components/GoalWidget';
import {
  buildSupportUrl, trackEmbedEvent, npubToPubkey,
  type WidgetType, type WidgetTheme,
} from '../lib/embed';

export default function EmbedWidgetPage() {
  const { npub } = useParams<{ npub: string }>();
  const [params] = useSearchParams();

  const type = (['card', 'button', 'progress'].includes(params.get('type') || '')
    ? params.get('type') : 'card') as WidgetType;
  const theme = (params.get('theme') === 'dark' ? 'dark' : 'light') as WidgetTheme;

  const creatorPubkey = useMemo(() => (npub ? npubToPubkey(npub) : ''), [npub]);
  const { price } = useBCHPrice();
  const goal = useGoalEmbedData(creatorPubkey || undefined, price);

  const containerRef = useRef<HTMLDivElement>(null);
  const donateUrl = useMemo(
    () => (creatorPubkey ? buildSupportUrl(creatorPubkey) : '#'),
    [creatorPubkey],
  );

  // ── Record a view once the goal resolves ──
  const viewedRef = useRef(false);
  useEffect(() => {
    if (goal.found && creatorPubkey && !viewedRef.current) {
      viewedRef.current = true;
      trackEmbedEvent('view', creatorPubkey);
    }
  }, [goal.found, creatorPubkey]);

  // ── Report height to the parent frame for auto-resize (no scrollbars) ──
  const frameId = `bchnostr-embed-${(npub || '').slice(4, 14)}-${type}`;
  useEffect(() => {
    const report = () => {
      const h = containerRef.current?.scrollHeight;
      if (!h) return;
      try {
        window.parent?.postMessage(
          { type: 'bchnostr-embed-resize', frameId, height: Math.ceil(h) },
          '*',
        );
      } catch { /* not embedded, or blocked — harmless */ }
    };
    report();
    const ro = new ResizeObserver(report);
    if (containerRef.current) ro.observe(containerRef.current);
    const t = setTimeout(report, 400);
    return () => { ro.disconnect(); clearTimeout(t); };
  }, [frameId, type, goal.found, goal.raisedSats, goal.pct]);

  // Record the click. Navigation itself is a real anchor (in GoalWidget) so it
  // works even inside sandboxed iframes where window.open is blocked.
  const handleDonateClick = () => {
    if (creatorPubkey) trackEmbedEvent('donate_click', creatorPubkey);
  };

  return (
    <GoalWidget
      ref={containerRef}
      type={type}
      theme={theme}
      loading={goal.loading}
      found={goal.found}
      data={goal.data}
      raisedSats={goal.raisedSats}
      targetSats={goal.targetSats}
      targetUsd={goal.targetUsd}
      pct={goal.pct}
      backerCount={goal.backerCount}
      price={price}
      donateUrl={donateUrl}
      hasCreator={!!creatorPubkey}
      onDonateClick={handleDonateClick}
    />
  );
}
